// The coverage page, both flows (Design round 3). Every number on this screen
// is the carrier's: the base total is Global's TotalAmount for the submitted
// config, every rung's delta is carrier premium minus the selected rung's
// premium off THIS quote's RatingResults (no requote to change a rung), the
// CFI extension is the live pair delta, and aircraft-type lines price by
// speculative rater calls (never a constant; an unconfirmed call renders as
// "quoted in a moment", never a number).
//
// School flow: the damage slider's minimum is PINNED at the school floor.
// /quote: full range with the "typical minimum" tag at the default, deltas
// negative-green below it, base rebased accordingly.
//
// Design's round-3 DC files for this screen have not synced to the repo; the
// tap-open info blurbs are CC-authored plain language pending Design's copy.
// The endorsement link points at Global's published C123 specimen
// (web-ctr/public/specimens/, round 3.1).
import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '@/lib/api.js';
import { C123_SPECIMEN_URL } from '@/lib/specimens.js';
import { FinalizingBlock } from '@/routes/portal/Outcome.jsx';
import tokens from '@/brand-tokens.json';

const money = (v) => `$${Number(v || 0).toLocaleString('en-US')}`;
const liabLabel = (r) => `${money(r.limit)}${r.excludingPassengers ? ' excl. passengers' : ` / ${money(r.paxSubLimit)} pax`}`;

/** Does a liability rung meet the school's floor (limit, and the per-passenger sublimit unless passengers are excluded)? */
function meetsLiabilityFloor(r, floor) {
  if (!floor) return true;
  if (Number(r.limit) < Number(floor.CoverageLimit || 0)) return false;
  if (floor.ExcludingPassengers) return true;
  return !r.excludingPassengers && Number(r.paxSubLimit || 0) >= Number(floor.PassengerSubLimit || 0);
}

const BLURBS = {
  liability: 'Pays for injury or property damage you cause to others while flying. The second number is the most it pays any one passenger.',
  damage: 'Pays for damage to the aircraft you rented. Without it, the repair bill for a rented airplane can land on you personally.',
  deductibleReimb: 'If the aircraft owner has a claim on their own policy, their deductible can be passed to you. This pays it back.',
  medical: 'Pays medical bills for you and your passengers after an accident, regardless of fault.',
  cfi: 'Extends your coverage to instructing: dual given, solo endorsements, and flight reviews. Without it the policy covers personal flying only.',
  types: 'Your policy covers the aircraft types you select. Single engine land and gliders are included in every policy; add the others only if you fly them.',
};

const TYPE_LINES = [
  ['MEL', 'Multi engine land'],
  ['SES', 'Seaplane (single engine sea)'],
  ['rotorcraft', 'Rotorcraft'],
];

function InfoTitle({ id, title, open, setOpen }) {
  return (
    <button type="button" onClick={() => setOpen(open === id ? null : id)} className="flex items-center gap-1.5 text-[13px] font-bold text-ink">
      {title} <span className="text-[11px] text-line-strong">ⓘ</span>
    </button>
  );
}

function Rung({ on, label, tag, price, negative, onPick }) {
  return (
    <button type="button" onClick={onPick}
      className={`flex min-h-[44px] w-full items-center gap-[11px] rounded-[11px] border-[1.5px] p-[11px_14px] text-left ${on ? 'border-accent bg-teal-card' : 'border-line bg-white'}`}>
      <span className={`h-[17px] w-[17px] flex-none rounded-pill border-[1.5px] ${on ? 'border-accent bg-accent shadow-[inset_0_0_0_3px_white]' : 'border-line-strong bg-white'}`} />
      <span className="flex-1 text-[13.5px] font-semibold text-ink">
        {label}
        {tag ? <span className="ml-[7px] rounded-pill bg-fill px-[7px] py-0.5 text-[9.5px] font-bold uppercase tracking-[0.04em] text-ink-dim">{tag}</span> : null}
      </span>
      <span className={`flex-none whitespace-nowrap text-[13px] font-bold ${negative ? 'text-ok-fg' : 'text-primary'}`}>{price}</span>
    </button>
  );
}

/**
 * props:
 *  flow: 'school' | 'direct'
 *  school: the themed school (school flow) or null/attributed info (direct)
 *  quote: the /quote response (total, ladder, floor, surcharge_percent,
 *         cfi_extension, quote_token, aircraft_types via answersTypes)
 *  answers: the step-2 answers (ratings drive speculative type prefetch)
 *  electedTypes: the step-1 aircraft types (direct) or school fleet types
 *  onCheckout(selection): fired with the final selection summary
 */
export default function Coverage({ flow, school, quote, answers, electedTypes, onCheckout }) {
  const [sel, setSel] = useState({ liability: null, damage: null, deductibleReimb: null, medical: null });
  const [seeAll, setSeeAll] = useState({});
  const [openInfo, setOpenInfo] = useState(null);
  const [autoRenew, setAutoRenew] = useState(true);
  const [finalizing, setFinalizing] = useState(false);
  const [cfiOn, setCfiOn] = useState(true);
  const [typeOn, setTypeOn] = useState({});
  const [typeDeltas, setTypeDeltas] = useState({}); // key → {delta, confirmed} | {pending} | {unpriceable}
  const prefetched = useRef(false);

  const ladder = useMemo(() => quote.ladder || {}, [quote]);
  const pct = Number(quote.surcharge_percent) || 0;
  const isSchool = flow === 'school';
  const elected = electedTypes || {};
  // Extra types are priced by speculative rater calls, which run in Global UAT only (in production each call is a real
  // quote Global emails the student about): the server says so, and lines it cannot price do not show.
  const missingTypes = quote.speculative_pricing === false ? [] : TYPE_LINES.filter(([key]) => !elected[key]);

  async function fetchTypeDelta(key) {
    if (!quote.quote_token) { setTypeDeltas((d) => ({ ...d, [key]: { unpriceable: true } })); return; }
    setTypeDeltas((d) => ({ ...d, [key]: { pending: true } }));
    try {
      const r = await api.post('/api/ctr/quote/type-delta', { quote_token: quote.quote_token, type: key });
      if (r.busy || r.pending) { setTimeout(() => fetchTypeDelta(key), 4000); return; }
      setTypeDeltas((d) => ({ ...d, [key]: r.confirmed ? { delta: r.delta, confirmed: true } : { unpriceable: true } }));
    } catch {
      setTypeDeltas((d) => ({ ...d, [key]: { unpriceable: true } }));
    }
  }

  // Speculative prefetch: fire for the types the pilot's RATINGS suggest they
  // fly (a rating is never required to buy; it only decides which calls are
  // worth firing early).
  useEffect(() => {
    if (prefetched.current || !quote.quote_token) return;
    prefetched.current = true;
    const ratings = (answers?.ratings || []);
    const suggest = new Set();
    if (ratings.includes('AMEL')) suggest.add('MEL');
    if (ratings.includes('ASES')) suggest.add('SES');
    if (ratings.includes('Helicopter')) suggest.add('rotorcraft');
    for (const [key] of missingTypes) if (suggest.has(key)) fetchTypeDelta(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote.quote_token]);

  const pick = (line, rung) => setSel((s) => ({ ...s, [line]: rung.is_floor ? null : rung }));
  const rungDeltas = Object.values(sel).filter(Boolean).reduce((a, r) => a + Number(r.delta || 0), 0);
  const cfiDelta = quote.cfi_extension && !cfiOn ? -Number(quote.cfi_extension.delta) : 0;
  const typeDelta = Object.entries(typeOn).filter(([, on]) => on)
    .reduce((a, [key]) => a + (typeDeltas[key]?.confirmed ? Number(typeDeltas[key].delta) : 0), 0);
  const total = Math.round(Number(quote.total) + rungDeltas * (1 + pct / 100) + cfiDelta + typeDelta);

  // Damage slider: the REAL rung list. School flow starts at the floor rung.
  const dmgRungs = useMemo(() => {
    const all = [...(ladder.damage || [])].sort((a, b) => Number(a.limit) - Number(b.limit));
    if (!isSchool) return all;
    const floorIdx = all.findIndex((r) => r.is_floor);
    return floorIdx >= 0 ? all.slice(floorIdx) : all;
  }, [ladder.damage, isSchool]);
  const dmgSelected = sel.damage || dmgRungs.find((r) => r.is_floor) || dmgRungs[0];
  const dmgIdx = Math.max(0, dmgRungs.findIndex((r) => r === dmgSelected));

  const pickerLines = useMemo(() => [
    {
      id: 'liability', title: 'Liability',
      note: isSchool ? `school minimum: ${ladder.liability?.find((r) => r.is_floor) ? liabLabel(ladder.liability.find((r) => r.is_floor)) : ''}` : 'your selection is the base',
      // School flow: only rungs that meet the school's liability AND its per-passenger minimum (a dearer rung can carry
      // a lower passenger sublimit; checkout refuses it, so it must not be offered).
      rungs: (ladder.liability || []).filter((r) => (isSchool ? (r.is_floor || (Number(r.delta) > 0 && meetsLiabilityFloor(r, quote.floor?.liability))) : true)),
      label: liabLabel,
    },
    {
      id: 'deductibleReimb', title: 'Deductible reimbursement',
      note: isSchool
        ? (quote.floor.deductible_reimb.required_by_school
          ? `your school requires ${money(quote.floor.deductible_reimb.CoverageLimit)}`
          : `${money(quote.floor.deductible_reimb.CoverageLimit)} included (not required by ${school?.name || 'your school'})`)
        : 'covers the owner’s deductible passed to you',
      rungs: (ladder.deductibleReimb || []).filter((r) => isSchool ? (r.is_floor || Number(r.delta) > 0) : true),
      label: (r) => money(r.limit),
      expander: isSchool && quote.floor.deductible_reimb.required_by_school,
    },
    {
      id: 'medical', title: 'Medical expense, for you and your passengers',
      note: `${money(quote.floor.medical.CoverageLimit)} included`,
      rungs: (ladder.medical || []).filter((r) => isSchool ? (r.is_floor || Number(r.delta) > 0) : true),
      label: (r) => money(r.limit),
    },
  ], [ladder, quote, school, isSchool]);

  const [dedOpen, setDedOpen] = useState(false);

  return (
    <>
      <div className="mb-[18px] mt-2 text-center">
        <div className="mb-1 text-[13px] text-ink-dim">{isSchool ? 'Your required coverage' : 'Your coverage, your call'}</div>
        <div className="text-[40px] font-bold tracking-[-0.03em]">
          {money(total)}<span className="text-[17px] font-semibold text-ink-dim">/yr</span>
        </div>
        <div className="mt-0.5 text-[12.5px] text-ink-faint">
          {isSchool
            ? `Everything ${school?.name || 'your school'} requires, exactly. About ${money(Math.round(total / 12))}/month, paid up front for the year.`
            : `Typical school minimums, change anything. About ${money(Math.round(total / 12))}/month, paid up front for the year.`}
        </div>
        {quote.cfi_extension && !cfiOn ? (
          <div className="mt-1 text-[12px] font-semibold text-warn-fg">(CFI extension declined)</div>
        ) : null}
      </div>

      {/* ── damage to the rented aircraft: the snapping slider ── */}
      <div className="mb-4">
        <div className="mb-[7px] flex items-baseline justify-between gap-2.5">
          <InfoTitle id="damage" title="Damage to the rented aircraft" open={openInfo} setOpen={setOpenInfo} />
          <span className="text-[11px] text-ink-faint">
            {isSchool ? `minimum: ${money(quote.floor.damage.HullLimit)} (your school's floor)` : ''}
          </span>
        </div>
        {openInfo === 'damage' ? <div className="mb-2 rounded-lg bg-teal-faint p-[9px_12px] text-xs leading-[1.5] text-teal-mute">{BLURBS.damage}</div> : null}
        {dmgRungs.length ? (
          <div className="rounded-[13px] border-[1.5px] border-line bg-white p-[14px_16px]">
            <div className="flex items-baseline justify-between">
              <span className="text-[19px] font-bold tracking-[-0.01em]">
                {money(dmgSelected?.limit)}
                {!isSchool && dmgSelected?.is_floor ? <span className="ml-2 rounded-pill bg-fill px-2 py-[2px] text-[9.5px] font-bold uppercase tracking-[0.04em] text-ink-dim">typical minimum</span> : null}
                {isSchool && dmgSelected?.is_floor ? <span className="ml-2 rounded-pill bg-fill px-2 py-[2px] text-[9.5px] font-bold uppercase tracking-[0.04em] text-ink-dim">school minimum</span> : null}
              </span>
              <span className={`text-[13px] font-bold ${Number(dmgSelected?.delta) < 0 ? 'text-ok-fg' : 'text-primary'}`}>
                {dmgSelected?.is_floor ? 'included' : `${Number(dmgSelected?.delta) < 0 ? '−' : '+'} ${money(Math.abs(dmgSelected?.delta))}/yr`}
              </span>
            </div>
            <input
              type="range" min="0" max={dmgRungs.length - 1} step="1" value={dmgIdx}
              aria-label="Damage to rented aircraft limit"
              onChange={(e) => pick('damage', dmgRungs[Number(e.target.value)])}
              className="mt-3 w-full"
              style={{ accentColor: `var(--school-accent, ${tokens.colors.accent})` }}
            />
            <div className="mt-1 flex justify-between text-[10.5px] text-ink-faint">
              <span>{money(dmgRungs[0]?.limit)}</span>
              <span>{money(dmgRungs[dmgRungs.length - 1]?.limit)}</span>
            </div>
          </div>
        ) : null}
      </div>

      {/* ── the rung pickers ── */}
      <div className="mb-[18px] flex flex-col gap-3.5">
        {pickerLines.map((ln) => {
          const selected = sel[ln.id];
          const showAll = !!seeAll[ln.id];
          const collapsed = ln.expander && !dedOpen;
          const visible = showAll ? ln.rungs : ln.rungs.slice(0, 3);
          return (
            <div key={ln.id}>
              <div className="mb-[7px] flex items-baseline justify-between gap-2.5">
                <InfoTitle id={ln.id} title={ln.title} open={openInfo} setOpen={setOpenInfo} />
                <span className="text-[11px] text-ink-faint">{ln.note}</span>
              </div>
              {openInfo === ln.id ? <div className="mb-2 rounded-lg bg-teal-faint p-[9px_12px] text-xs leading-[1.5] text-teal-mute">{BLURBS[ln.id]}</div> : null}
              {collapsed ? (
                <button type="button" onClick={() => setDedOpen(true)}
                  className="flex min-h-[44px] w-full items-center justify-between rounded-[11px] border-[1.5px] border-line bg-white p-[11px_14px] text-left">
                  <span className="text-[13.5px] font-semibold">{money(quote.floor.deductible_reimb.CoverageLimit)} <span className="ml-1.5 rounded-pill bg-fill px-[7px] py-0.5 text-[9.5px] font-bold uppercase tracking-[0.04em] text-ink-dim">school requirement</span></span>
                  <span className="text-xs font-semibold text-accent">Change</span>
                </button>
              ) : (
                <>
                  <div className="flex flex-col gap-[7px]">
                    {visible.map((r) => {
                      const on = selected ? selected === r : r.is_floor;
                      const neg = Number(r.delta) < 0;
                      return (
                        <Rung key={`${r.limit}-${r.paxSubLimit ?? ''}-${r.excludingPassengers ?? ''}`}
                          on={on}
                          label={ln.label(r)}
                          tag={r.is_floor ? (ln.id === 'medical' ? 'included' : isSchool ? 'school minimum' : 'selected') : null}
                          negative={neg}
                          price={r.is_floor ? 'included' : `${neg ? '−' : '+'} ${money(Math.abs(r.delta))}/yr`}
                          onPick={() => pick(ln.id, r)}
                        />
                      );
                    })}
                  </div>
                  {ln.rungs.length > 3 ? (
                    <button type="button" onClick={() => setSeeAll((s) => ({ ...s, [ln.id]: !s[ln.id] }))}
                      className="pt-1.5 text-xs font-semibold text-accent hover:text-primary">
                      {showAll ? 'Show fewer options' : `See all ${ln.rungs.length} options`}
                    </button>
                  ) : null}
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* ── CFI extension (deselectable checked line) ── */}
      {quote.cfi_extension ? (
        <div className="mb-4">
          <div className="mb-[7px] flex items-baseline justify-between gap-2.5">
            <InfoTitle id="cfi" title="CFI extension" open={openInfo} setOpen={setOpenInfo} />
            <a href={C123_SPECIMEN_URL} target="_blank" rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-[11.5px] font-semibold text-accent">Read the endorsement</a>
          </div>
          {openInfo === 'cfi' ? <div className="mb-2 rounded-lg bg-teal-faint p-[9px_12px] text-xs leading-[1.5] text-teal-mute">{BLURBS.cfi}</div> : null}
          <button type="button" onClick={() => setCfiOn((v) => !v)}
            className={`flex min-h-[48px] w-full items-center gap-[11px] rounded-[11px] border-[1.5px] p-[11px_14px] text-left ${cfiOn ? 'border-accent bg-teal-card' : 'border-line bg-white'}`}>
            <span className={`flex h-[18px] w-[18px] flex-none items-center justify-center rounded-[4px] border-[1.5px] text-[12px] font-bold text-white ${cfiOn ? 'border-accent bg-accent' : 'border-line-strong bg-white'}`}>{cfiOn ? '✓' : ''}</span>
            <span className="flex-1 text-[13.5px] font-semibold">Instructing covered (CFI extension)</span>
            <span className="flex-none text-[13px] font-bold text-primary">+ {money(quote.cfi_extension.delta)}/yr</span>
          </button>
          {!cfiOn ? (
            <div className="mt-2 rounded-[9px] bg-warn-bg p-[10px_12px] text-xs leading-[1.5] text-warn-deep">
              <b>Personal flying only.</b> Without the extension this policy does not cover you while instructing, giving dual, or signing anyone off.
            </div>
          ) : null}
        </div>
      ) : null}

      {/* ── more aircraft types (speculative pricing) ── */}
      {missingTypes.length ? (
        <div className="mb-[18px]">
          <div className="mb-[7px]">
            <InfoTitle id="types" title="More aircraft types" open={openInfo} setOpen={setOpenInfo} />
          </div>
          {openInfo === 'types' ? <div className="mb-2 rounded-lg bg-teal-faint p-[9px_12px] text-xs leading-[1.5] text-teal-mute">{BLURBS.types}</div> : null}
          <div className="flex flex-col gap-[7px]">
            {missingTypes.map(([key, label]) => {
              const d = typeDeltas[key];
              const on = !!typeOn[key];
              const price = d?.confirmed ? `+ ${money(d.delta)}/yr`
                : d?.pending ? 'quoted in a moment…'
                : d?.unpriceable ? 'we’ll confirm this price by email'
                : 'tap to price';
              return (
                <button key={key} type="button"
                  onClick={() => {
                    if (!d) fetchTypeDelta(key);
                    if (d?.confirmed) setTypeOn((t) => ({ ...t, [key]: !t[key] }));
                    else if (!d) setTypeOn((t) => ({ ...t, [key]: !t[key] }));
                  }}
                  className={`flex min-h-[48px] w-full items-center gap-[11px] rounded-[11px] border-[1.5px] p-[11px_14px] text-left ${on && d?.confirmed ? 'border-accent bg-teal-card' : 'border-line bg-white'}`}>
                  <span className={`flex h-[18px] w-[18px] flex-none items-center justify-center rounded-[4px] border-[1.5px] text-[12px] font-bold text-white ${on && d?.confirmed ? 'border-accent bg-accent' : 'border-line-strong bg-white'}`}>{on && d?.confirmed ? '✓' : ''}</span>
                  <span className="flex-1 text-[13.5px] font-semibold">{label}</span>
                  <span className="flex-none text-[12.5px] font-bold text-primary">{price}</span>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {/* ── sticky total + checkout ── */}
      <div className="sticky bottom-0 -mx-1.5 mt-auto rounded-t-modal border border-line bg-white p-[18px_20px] shadow-bar">
        {pct > 0 ? (
          <div className="mb-1.5 flex justify-between text-xs text-ink-dim"><span>State surcharge</span><span>{pct}%, included in the total</span></div>
        ) : null}
        <div className="flex justify-between text-[15px] font-bold">
          <span>Total</span>
          <span>{money(total)}<span className="text-xs font-semibold text-ink-faint">/yr</span></span>
        </div>
        <div className="mt-[1px] text-right text-[11.5px] text-ink-faint">about {money(Math.round(total / 12))}/month · one annual payment, up front</div>
        <div className={`my-3 flex items-start gap-2.5 rounded-card p-[11px_13px] text-xs leading-[1.5] ${autoRenew ? 'bg-ok-bg text-ok-fg' : 'bg-fill text-slate'}`}>
          <span className="flex-none font-bold">{autoRenew ? '✓' : '·'}</span>
          <span>
            <b>{autoRenew ? 'Auto-renew is on.' : 'Auto-renew is off.'}</b>{' '}
            {autoRenew ? "You'll never show up to the counter with lapsed coverage." : 'Your coverage ends in 12 months unless you renew it yourself.'}{' '}
            <button type="button" onClick={() => setAutoRenew((v) => !v)} className="font-bold text-accent underline">
              {autoRenew ? 'Turn it off' : 'Turn it back on'}
            </button>
          </span>
        </div>
        {finalizing ? <FinalizingBlock /> : (
          <button type="button" onClick={() => {
            const typesConfirmed = Object.entries(typeOn).filter(([key, on]) => on && typeDeltas[key]?.confirmed).map(([key]) => key);
            // Every line explicit (the floor rung where nothing was picked): checkout compares the WHOLE config with the
            // quote Global holds, so what this page shows is what the payment page prices.
            const floorOf = (k) => (ladder[k] || []).find((r) => r.is_floor) || null;
            const chosen = {
              liability: sel.liability || floorOf('liability'),
              damage: sel.damage || floorOf('damage'),
              deductibleReimb: sel.deductibleReimb || floorOf('deductibleReimb'),
              medical: sel.medical || floorOf('medical'),
            };
            const done = onCheckout?.({ total, autoRenew, cfiOn, typeOn, typesConfirmed, sel, chosen });
            if (done !== 'handled') setFinalizing(true);
          }}
            className="min-h-[52px] w-full rounded-card-lg bg-accent text-base font-bold text-white hover:bg-accent-hover">
            Continue to secure checkout →
          </button>
        )}
        <div className="mt-2.5 text-[11px] leading-[1.55] text-ink-faint">
          Payment is collected securely by Global Aerospace. Global issues your certificate{school?.name ? ` and sends it to ${school.name} automatically` : '; if you named a school or club, it lands on their dashboard automatically'}.
          Nothing to print, nothing to forward.{' '}
          <a href="/policy-diff" target="_blank" rel="noreferrer" className="font-semibold text-accent">Full plain-English summary</a>
        </div>
      </div>
    </>
  );
}
